import React from 'react'
import { makeStyles } from '@mui/styles'
import { Drawer } from '@mui/material'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { brandHeading } from '@/utils/brand-typography'
import { ButtonSquare } from '@/modules/base/components/buttons'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

// the FAB and the close button share one spot, so the drawer is closed where it was opened
const BUTTON_SIZE = 48

const useStyles = makeStyles((theme: ThemeInterface) => ({
  roundButton: {
    position: 'relative',
    flexShrink: 0,
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    padding: 0,
    borderRadius: theme.borderRadiusPill,
    // the count badge sticks out of the button
    overflow: 'visible',
  },
  fab: {
    background: theme.palette.brand.main,
    color: theme.palette.brand.contrastText,
    '&:hover': {
      background: theme.palette.brand.dark,
      color: theme.palette.brand.contrastText,
    },
  },
  count: {
    position: 'absolute',
    top: -4,
    right: -4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    height: 20,
    padding: '0 4px',
    border: `2px solid ${theme.palette.background.paper}`,
    borderRadius: theme.borderRadiusPill,
    background: theme.palette.brand.main,
    color: theme.palette.brand.contrastText,
    fontSize: theme.brandTypography.text.tiny.fontSize,
    fontWeight: theme.brandTypography.weights.semibold,
  },
  // the checkout itself is a full screen dialog - a drawer's default z-index would open behind it
  // (doubled class, so it wins over the drawer's own emotion style regardless of the injection order)
  drawer: {
    '&.MuiDrawer-root': {
      zIndex: theme.zIndex.modal,
    },
  },
  paper: {
    width: '100%',
    fontFamily: theme.brandFontFamilies.body,
    color: theme.palette.brand.text.primary,
  },
  // same height and padding as the checkout header, so the close button sits where the FAB was
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.brand.divider}`,
  },
  title: {
    margin: 0,
    ...brandHeading(theme, 'h6'),
    color: theme.palette.brand.text.heading,
  },
  close: {
    background: theme.backgroundColor,
    color: theme.palette.brand.text.heading,
    '&:hover': {
      background: theme.palette.brand.light,
      color: theme.palette.brand.text.heading,
    },
  },
}))

interface SelectionDrawerProps {
  open: boolean
  // the products in the selection, shown as the FAB's badge
  count: number
  onOpen: () => void
  onClose: () => void
  // the selection sidebar - shown in full screen
  children: React.ReactNode
}

/**
 * Mobile: "Deine Auswahl" is a FAB with a shopping cart in the header, which opens the selection in full screen.
 */
const SelectionDrawer = ({ open, count, onOpen, onClose, children }: SelectionDrawerProps) => {
  const classes = useStyles()
  const texts = lang.upgrade.wizard.sidebar

  return (
    <>
      <ButtonSquare
        className={`${classes.roundButton} ${classes.fab}`}
        aria-label={texts.open(count)}
        aria-expanded={open}
        dataQsId='checkout-selection-open'
        onClick={onOpen}>
        <ShoppingCartOutlinedIcon />
        {count > 0 && <span className={classes.count} aria-hidden data-qs-id='checkout-selection-count'>{count}</span>}
      </ButtonSquare>
      <Drawer
        anchor='right'
        open={open}
        onClose={onClose}
        className={classes.drawer}
        classes={{ paper: classes.paper }}
        data-qs-id='checkout-selection-drawer'>
        <div className={classes.header}>
          <h2 className={classes.title}>{texts.title}</h2>
          <ButtonSquare
            className={`${classes.roundButton} ${classes.close}`}
            aria-label={texts.close}
            dataQsId='checkout-selection-close'
            onClick={onClose}>
            <CloseRoundedIcon />
          </ButtonSquare>
        </div>
        {children}
      </Drawer>
    </>
  )
}

export default SelectionDrawer
